import React, { useContext, useEffect, useState } from 'react'
import useAxiosPrivate from '../../../../../Hooks/useAxiosPrivate';
import useAuth from '../../../../../Hooks/useAuth';
import CustomizerContext from '../../../../../_helper/Customizer';
import GeneralLedgerAccountList, { StatusRow } from './GeneralLedgerAccountList';
import { Code, DateCreated, DateModified, Description, Edit, GeneralLedgerAccount, GeneralLedgerClass, GeneralLedgerGroup, GeneralLedgerType, LegacyNumber, MaReportAccount, MaReportClass, MaReportType, Name, Status, View } from '../../../../../Constant';
import { GlAccountFormDataPayload, GlGroup, IGeneralLedgerAccountResponse } from '../GeneralLedgerAccount.types';
import { formatDateWithTimeZone } from '../../../../../_helper/dateHelper';
import { FinanceMaReportAccountApi, FinanceMaReportClassApi, FinanceMaReportTypeApi, financesGeneralLedgerAccountApi, financesGeneralLedgerClassApi, financesGeneralLedgerGroupApi, financesGeneralLedgerTypeApi } from '../../../../../api';
import { toast } from 'react-toastify';
import { InputProviderFormSchema } from '../../../Template/types';
import { validateInputs } from '../../../Template/InputProvider';

export default function useGeneralLedgerAccountListController() {
    const axiosPrivate = useAxiosPrivate();
    const { userOrganisationId }: any = useAuth();
    const { layoutURL } = useContext(CustomizerContext);
    const newBtn = `${process.env.PUBLIC_URL}/finance/settings/general-ledger-account/create/${layoutURL}`;
    const title = GeneralLedgerAccount;

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<IGeneralLedgerAccountResponse[]>([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<
        (typeof data)[0] | null
    >(null);
    const [editMode, setEditMode] = useState(false);

    const MaReportAccountTab: string = "ma-report-account-tab";
    const [activeTab, setActiveTab] = useState<string>(MaReportAccountTab); 
    const handleTabClick = (tabName: string) => {
        setActiveTab(tabName);
    };

    function fetchData() {
        setLoading(true);
        axiosPrivate
            .get(
                `${financesGeneralLedgerAccountApi}?organisation_id=${userOrganisationId}`
            )
            .then((res) => {
                const response: IGeneralLedgerAccountResponse[] = res.data.items;
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 404) {
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! Something went wrong");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() =>{
        fetchData()
    }, [])

    const [switchLabels, setSwitchLabels] = useState([
        { id: 1, label: Status, value: false },
    ]);

    //======== Form Input Error ===========
    const [inputErrors, setInputErrors] = useState<{
        glAccountFormErrors: { [key: string]: string };
        maAccountFormErrors: { [key: string]: string };
      }>({
        glAccountFormErrors: {},
        maAccountFormErrors: {},
    });

    //======== Form Input Schema ===========
    const [glAccountFormSchema, setGlAccountFormSchema] = useState<
        InputProviderFormSchema[]
    >([
        {
            name: "gl_group_id",
            label: GeneralLedgerGroup,
            type: "select",
            required: true,
        },
        {
            name: "gl_class_id",
            label: GeneralLedgerClass,
            type: "select",
            required: true,
        },
        {
            name: "gl_type_id",
            label: GeneralLedgerType,
            type: "select",
            required: true,
        },
        {
            name: "name",
            label: Name,
            type: "text",
            required: true,
        },
        {
            name: "legacy_number",
            label: LegacyNumber,
            type: "text",
            required: false,
        },
        {
            name: "description",
            label: Description,
            type: "textarea",
            required: true,
        }
    ,]);

    const [maAccountFormSchema, setMaAccountFormSchema] = useState<
        InputProviderFormSchema[]
    >([
        {
            name: "ma_report_class_id",
            label: MaReportClass,
            type: "select",
            required: true,
        },
        {
            name: "ma_report_type_id",
            label: MaReportType,
            type: "select",
            required: true,
        },
        {
            name: "ma_report_account_id",
            label: MaReportAccount,
            type: "select",
            required: true,
        },
    ]);

    //======== Form Input Data ===========
    const [glAccountInputData, setGlAccountInputData] = useState<{
        [key: string]: any;
    }>({});

    const [maAccountInputData, setMaAccountInputData] = useState<{
        [key: string]: any;
    }>({});

    //======== Form OnChange ===========
    const handleGlAccountChange = (name: string, value: any) => {
        setGlAccountInputData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        if (name === "gl_group_id") {
            getGlClass(value);
            getMaReportClass(value);
        }
        name === "gl_class_id" && getGlType(value);
    };
    
    const handleMaAccountChange = (name: string, value: any) => {
        setMaAccountInputData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        
        name === "ma_report_class_id" && getMaReportType(value);
        name === "ma_report_type_id" && getMaReportAccount(value);
    };

    const getGlClass = async (value: number) => {
        const api = `${financesGeneralLedgerClassApi}?organisation_id=${userOrganisationId}&gl_group_id=${value}`;

        setGlAccountFormSchema((prevSchema) =>
            prevSchema.map((field) => {
                if (field.name === "gl_class_id") {
                    return {
                        ...field,
                        options: [],
                    };
                }
                return field; 
            })
        );

        if (!value) {
            getGlType(0)
            return
        } 

        try {
            axiosPrivate.get(api).then((res) => {
                const response: any[] = res.data;
                
                setGlAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "gl_class_id") {
                            return {
                                ...field,
                                options: response.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field; 
                    })
                );

            })
            .catch((err) => {
                
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if ([401, 404].indexOf(err.response.status) !== -1) { 
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! cannot fetch general ledger class");
                }
            });
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally{
            getGlType(0)
        }
    };

    const getGlType = async (value: number) => {
        const api = `${financesGeneralLedgerTypeApi}?organisation_id=${userOrganisationId}&gl_class_id=${value}`;

        setGlAccountFormSchema((prevSchema) =>
            prevSchema.map((field) => {
                if (field.name === "gl_type_id") {
                    return {
                        ...field,
                        options: [],
                    };
                }
                return field; 
            })
        );

        if (!value) {
            return
        }   

        try {
            axiosPrivate.get(api).then((res) => {
                const response: any[] = res.data;
                
                setGlAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "gl_type_id") {
                            return {
                                ...field,
                                options: response.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field; 
                    })
                );
            })
            .catch((err) => {
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if ([401, 404].indexOf(err.response.status) !== -1) { 
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! cannot fetch general ledger type");
                }
            });
        } catch (error) {
            //console.error("Error fetching data:", error);
        }
    };

    const getMaReportClass = async (value: number) => {
        const api = `${FinanceMaReportClassApi}/cb?organisation_id=${userOrganisationId}&gl_group_id=${value}`;

        setMaAccountFormSchema((prevSchema) =>
            prevSchema.map((field) => {
                if (field.name === "ma_report_class_id") {
                    return {
                        ...field,
                        options: [],
                    };
                }
                return field; 
            })
        );

        if (!value) {
            getMaReportType(0)
            getMaReportAccount(0)
            return
        } 

        try {
            axiosPrivate.get(api).then((res) => {
                const response: any[] = res.data;
                
                setMaAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "ma_report_class_id") {
                            return {
                                ...field,
                                options: response.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field; 
                    })
                );

            })
            .catch((err) => {
                
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if ([401, 404].indexOf(err.response.status) !== -1) { 
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! cannot fetch management account report class");
                }
            });
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally{
            getMaReportType(0)
            getMaReportAccount(0)
        }
    };

    const getMaReportType = async (value: number) => {
        const api = `${FinanceMaReportTypeApi}/cb?organisation_id=${userOrganisationId}&ma_report_class_id=${value}`;

        setMaAccountFormSchema((prevSchema) =>
            prevSchema.map((field) => {
                if (field.name === "ma_report_type_id") {
                    return {
                        ...field,
                        options: [],
                    };
                }
                return field; 
            })
        );

        if (!value) {
            getMaReportAccount(0)
            return
        } 

        try {
            axiosPrivate.get(api).then((res) => {
                const response: any[] = res.data;
                
                setMaAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "ma_report_type_id") {
                            return {
                                ...field,
                                options: response.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field; 
                    })
                );

            })
            .catch((err) => {
                
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if ([401, 404].indexOf(err.response.status) !== -1) { 
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! cannot fetch management account report type");
                }
            });
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally{
            getMaReportAccount(0)
        }
    };

    const getMaReportAccount = async (value: number) => {
        const api = `${FinanceMaReportAccountApi}/cb?organisation_id=${userOrganisationId}&ma_report_type_id=${value}`;

        setMaAccountFormSchema((prevSchema) =>
            prevSchema.map((field) => {
                if (field.name === "ma_report_account_id") {
                    return {
                        ...field,
                        options: [],
                    };
                }
                return field; 
            })
        );

        if (!value) {
            return
        } 

        try {
            axiosPrivate.get(api).then((res) => {
                const response: any[] = res.data;
                
                setMaAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "ma_report_account_id") {
                            return {
                                ...field,
                                options: response.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field; 
                    })
                );

            })
            .catch((err) => {
                
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if ([401, 404].indexOf(err.response.status) !== -1) { 
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! cannot fetch management account report account");
                }
            });
        } catch (error) {
            //console.error("Error fetching data:", error);
        } 
    };

    const fetchGlGroupData = async () => {
        try {
            const financesGeneralLedgerGroupsUrl = `${financesGeneralLedgerGroupApi}?with_trashed=false&organisation_id=${userOrganisationId}`;

            const results = await Promise.allSettled([
                axiosPrivate.get(financesGeneralLedgerGroupsUrl),
            ]);
            
            const financesGeneralLedgerGroupsData: GlGroup[] = results[0].status === 'fulfilled' ? results[0].value.data : null;
            
            return {
                financesGeneralLedgerGroupsData,
            };
        } catch (error) {
            //console.error("Error fetching data:", error);
        }
    };

    useEffect(() =>{
        fetchGlGroupData().then((data) => {
            if(data) {
                setGlAccountFormSchema((prevSchema) =>
                    prevSchema.map((field) => {
                        if (field.name === "gl_group_id") {
                            return {
                                ...field,
                                options: data.financesGeneralLedgerGroupsData.map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                })),
                            };
                        }
                        return field;
                    })
                );
            }
        })
    },[]);

    const handleView = (row: (typeof data)[0]) => {
        setViewModalOpen(true);
        setEditMode(false);
        setSelectedRowData(row);
    };

    const tableColumns = [
        {
            name: Name,
            selector: (row: (typeof data)[0]) => `${row.name}`,
            sortable: true,
            center: false,
        },

        {
            name: Code,
            selector: (row: (typeof data)[0]) => `${row.number}`,
            sortable: true,
            center: false,
        },

        {
            name: Description,
            selector: (row: (typeof data)[0]) => `${row.description}`,
            sortable: false,
            center: false,
        },

        {
            name: GeneralLedgerType,
            selector: (row: (typeof data)[0]) => `${row.gl_type.name}`,
            sortable: true,
            center: false,
        },

        {
            name: GeneralLedgerClass,
            selector: (row: (typeof data)[0]) => `${row.gl_type.gl_class.name}`,
            sortable: true,
            center: false,
        },

        {
            name: GeneralLedgerGroup,
            selector: (row: (typeof data)[0]) =>
                `${row.gl_type.gl_class.gl_group.name}`,
            sortable: true,
            center: false,
        },

        {
            name: Status,
            selector: (row: (typeof data)[0]) => `${row.is_active}`,
            sortable: true,
            center: true,
            cell: (row: (typeof data)[0]) => StatusRow(row),
        },

        {
            name: DateCreated,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.created_at)}`,
            sortable: false,
            center: false,
        },

        {
            name: DateModified,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.updated_at)}`,
            sortable: false,
            center: false,
        },
    ];

    const OpenEditModal = async (row: (typeof data)[0]) => {
        setEditModalOpen(true);
        setEditMode(false);
        
        const glAccountEditData = {
            gl_group_id: row.gl_type.gl_class.gl_group.id,
            gl_class_id: row.gl_type.gl_class.id,
            gl_type_id: row.gl_type.id,
            name: row.name,
            legacy_number: row.legacy_number,
            description: row.description,
        };

        const maAccountEditData = {
            ma_report_class_id: row.finance_ma_report_account?.finance_ma_report_type.finance_ma_report_class.id,
            ma_report_type_id: row.finance_ma_report_account?.finance_ma_report_type.id,
            ma_report_account_id: row.finance_ma_report_account?.id,
        };
        
        if ((glAccountEditData.gl_group_id ?? 0) > 0) {
            getGlClass(Number(glAccountEditData.gl_group_id));
            getMaReportClass(Number(glAccountEditData.gl_group_id));
        }

        if ((glAccountEditData.gl_class_id ?? 0) > 0) {
            getGlType(Number(glAccountEditData.gl_class_id));
        }

        if ((maAccountEditData.ma_report_type_id ?? 0) > 0) {
            getMaReportType(Number(maAccountEditData.ma_report_type_id));
        }

        if ((maAccountEditData.ma_report_account_id ?? 0) > 0) {
            getMaReportAccount(Number(maAccountEditData.ma_report_account_id));
        }

        setGlAccountInputData(glAccountEditData);
        setMaAccountInputData(maAccountEditData);

        const switchEditData = [
            { id: 1, label: Status, value: row.is_active },
        ]

        setSwitchLabels(switchEditData)

        setSelectedRowData(row);
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => { 
        event.preventDefault(); 
            
        const id = selectedRowData!.id;

        setInputErrors({
            glAccountFormErrors: {},
            maAccountFormErrors: {},
        })

        const glAccountFormValidation = validateInputs(glAccountFormSchema, glAccountInputData);
        const maAccountFormValidation = validateInputs(maAccountFormSchema, maAccountInputData);
        
        if (!glAccountFormValidation.isValid) {
            setInputErrors(prev => ({ ...prev, glAccountFormErrors: glAccountFormValidation.errors }));
            return;
        }

        if (!maAccountFormValidation.isValid) {
            setInputErrors(prev => ({ ...prev, maAccountFormErrors: maAccountFormValidation.errors }));
            return;
        }

        const payload: GlAccountFormDataPayload = {
            organisation_id: userOrganisationId,
            gl_type_id: glAccountInputData.gl_type_id,
            finance_ma_report_account_id: maAccountInputData.ma_report_account_id,
            name: glAccountInputData.name,
            legacy_number: glAccountInputData.legacy_number,
            description: glAccountInputData.description,
            is_active: switchLabels[0].value
        }

        await axiosPrivate.put(`${financesGeneralLedgerAccountApi}/${id}`, payload).then((resp) => {
            const updatedData = data.map((row) =>
                row.id === selectedRowData!.id ? { ...row, ...resp.data } : row
            );

            setData(updatedData);

            toast.success(`${resp.data.name} is successfully updated.`)
        })
        .catch((err) => {
            if (err.response && err.response.status === 422) {
              toast.error("Validation Errors");
            } else if (err.response && err.response.status === 400) {
              toast.error(err.response.data.detail);
            } else {
              toast.error("Whoops! cannot update general ledger account");
            }
        });
    }

    const useTableAction = [
        {
          label: `${View}`,
          action: handleView,
          color: 'success',
          icon: 'icon-eye',
        },
        {
          label: `${Edit}`,
          action: OpenEditModal,
          color: 'warning',
          icon: 'icon-pencil-alt',
        },
    ];
    
    return {
        loading,
        title,
        newBtn,
        data,
        tableColumns,
        useTableAction,
        viewModalOpen,
        setViewModalOpen,
        selectedRowData,
        editMode,
        handleView,
        editModalOpen,
        setEditModalOpen,

        inputErrors,

        glAccountFormSchema,
        glAccountInputData,
        handleGlAccountChange,

        maAccountFormSchema,
        maAccountInputData,
        handleMaAccountChange,

        switchLabels,
        setSwitchLabels,

        MaReportAccountTab,
        activeTab,
        handleTabClick,

        handleSubmit
    }
    
}
